import {calcUserScore} from 'helpers/common-helper';
import BasePlayer from 'services/sports/default/base-player';
import {getDefaultExposure} from 'helpers/player-helper';
import Const from 'constants';
import Action from 'actions';
import {PlayersPoolBuilder} from 'services/players-pool/players-pool-builder';
import {getPlayerProjOwnFunctionality} from 'helpers/projected-ownership';
import {toFloat} from 'helpers/number-helper';

export default class Player extends BasePlayer {
    modes = [Const.CAPTAIN_MODE, Const.FLEX_MODE];

    getExpandedEntityParams({incomingValues = {}}) {
        const {modesConfig} = Action.getState();
        const {userPointsType = 'adminPoints'} = incomingValues;
        const expandedPlayer = super.getExpandedEntityParams({incomingValues});
        const userPoints = parseFloat(expandedPlayer[userPointsType]);
        const cptPointCoef = modesConfig.getConfig('CPT').getPointCoefficient();
        const captainUserPoints = userPoints * cptPointCoef;
        const captainAdminPoints = expandedPlayer.adminPoints * cptPointCoef;
        const captainPoints = expandedPlayer.points * cptPointCoef;
        const captainFptsWin = expandedPlayer.fptsWin * cptPointCoef;
        const captainFantasyPoints = expandedPlayer.fantasyPoints * cptPointCoef;
        const captainSalary = expandedPlayer.salary * cptPointCoef;
        const PlayerService = this;

        return {
            ...expandedPlayer,
            userPoints,
            userScore: calcUserScore(userPoints, expandedPlayer.salary, this.userScoreMultiplier),
            overall_exposureMax: getDefaultExposure('max'),
            lineupRole: Const.DEFAULT_MODE,
            captain_exposureMax: expandedPlayer.exposureMax,
            captain_exposureMin: expandedPlayer.exposureMin,
            captain_fantasyPoints: toFloat(captainFantasyPoints, 3),
            captain_points: toFloat(captainPoints, 3),
            captain_adminPoints: toFloat(captainAdminPoints, 3),
            captain_userPoints: toFloat(captainUserPoints, 3),
            captain_fptsWin: toFloat(captainFptsWin, 3),
            captain_userScore: calcUserScore(captainUserPoints, captainSalary, this.userScoreMultiplier),
            captain_salary: captainSalary,
            captain_pointsChangedManually: false,
            projOwn: {...getPlayerProjOwnFunctionality(expandedPlayer)},
            resetExposure() {
                this.overall_exposureMax = getDefaultExposure('max');
                this.exposureMin = getDefaultExposure('min');
                this.exposureMax = getDefaultExposure('max');
                this.captain_exposureMin = getDefaultExposure('min');
                this.captain_exposureMax = getDefaultExposure('max');
            },
            getPropNameOfMode(prop, incomingMode) {
                const mode = incomingMode || this.mode;

                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getPropNameOfLineupRole(prop) {
                return this.getIsCaptain() ? this.getCapPropName(prop) : prop;
            },
            getPropValueOfLineupRole(prop) {
                if (prop === 'projected_ownership') {
                    return this.projOwn.getValue(this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE);
                }

                let value,
                    propName = this.getPropNameOfLineupRole(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getCapPropName(prop) {
                const mode = this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE;

                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getCapPropValue(prop) {
                if (prop === 'projected_ownership') {
                    return this.projOwn.getValue(Const.CAPTAIN_MODE);
                }

                let value,
                    propName = this.getCapPropName(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getIsCaptain() {
                return this.lineupRole === Const.CAPTAIN_MODE;
            },
            setAsCaptain() {
                this.lineupRole = Const.CAPTAIN_MODE;
            },
            getLineupRole() {
                return this.lineupRole;
            },
        };
    }

    definePropNameOfMode({incomingValues: {prop, mode}}) {
        let captainPropsList = [
            'exposureMax',
            'exposureMin',
            'points',
            'salary',
            'adminPoints',
            'userPoints',
            'userScore',
            'fptsWin',
            'fantasyPoints',
            'pointsChangedManually',
        ];
        const overallPropsList = ['exposureMax'];
        if (Const.CAPTAIN_MODE === mode && inArray(prop, captainPropsList)) {
            return 'captain_' + prop;
        } else if (Const.OVERALL_MODE === mode && inArray(prop, overallPropsList)) {
            return 'overall_' + prop;
        }
        return prop;
    }

    preparePoolsOfPlayers({incomingValues: {players, filterPlayersBeforePreparingPools}}) {
        return this.exec('createPoolsOfPlayers', {
            players: Object.values(players),
        });
    }

    prepareRandomPoints({incomingValues: {}}) {
        return this.exec('prepareRandomPointsByModes', {});
    }

    createPoolsOfPlayers({incomingValues: {players}}) {
        const {playersLock} = Action.getState();

        let builder = new PlayersPoolBuilder(players);

        builder.addTaskOnScenario('position');
        builder.addTaskOnScenario('playerId');
        builder.addTaskOnScenario('teamAndPosition');
        builder.addTaskOnScenario('allowedPositions');
        builder.addTaskOnProperty('team');
        builder.addTaskOnProperty('gameInfo');
        builder.addTaskOnProperty('competitionName');
        builder.addTaskOnProperty('exposureMin', function (player) {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMin > 0
            );
        });
        builder.addTaskOnProperty('exposureMax', function (player) {
            return (
                !player.isExcludedByPoints(Const.FLEX_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.exposureMax < 100
            );
        });
        builder.addTaskOnProperty('captain_exposureMin', function (player) {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMin > 0
            );
        });
        builder.addTaskOnProperty('captain_exposureMax', function (player) {
            return (
                !player.isExcludedByPoints(Const.CAPTAIN_MODE) &&
                !playersLock.isLockedAnywhere(player.getId()) &&
                player.captain_exposureMax < 100
            );
        });
        builder.addTaskOnProperty('overall_exposureMax', function (player) {
            return !playersLock.isLockedAnywhere(player.getId()) && player.overall_exposureMax < 100;
        });

        return builder.execTasks();
    }

    preparePlayerForLineup({incomingValues = {}}) {
        return this.exec('preparePlayerForLineup_ShowDown', incomingValues);
    }

    resetPoints({incomingValues: {dispatch}}) {
        return this.exec('resetPoints_ShowDown', {dispatch, modes: this.modes});
    }

    resetSettings({incomingValues: {dispatch}}) {
        return this.exec('resetSettings_ShowDown', {dispatch});
    }

    preparePlayerDataForDownloading({incomingValues: {player}}) {
        return this.exec('preparePlayerDataForDownloading_ShowDown', {player});
    }

    getExtraPlayerDataToStore({...rest}) {
        return this.exec('getExtraPlayerDataToStore_ShowDown', {});
    }

    loadExtraPlayerDataFromStore({incomingValues: {dispatch, data}}) {
        return this.exec('loadExtraPlayerDataFromStore_ShowDown', {dispatch, data});
    }

    updatePlayersAfterRefreshingProjections({incomingValues: {indexedPlayers}}) {
        return this.exec('updatePlayersAfterRefreshingProjections_ShowDown', {indexedPlayers});
    }

    applySympathy({incomingValues: {players, playersSympathy}}) {
        return this.exec('applySympathy_ShowDown', {players, playersSympathy});
    }
}
